import {
  ShieldCheck,
  TrendingUp,
  SlidersHorizontal,
  PackageCheck,
  XOctagon,
  Target,
  Cpu,
  BookOpen,
} from "lucide-react";
import { useI18n } from "../../i18n";

// SEO long-form section — premium card-grid layout.
// All copy lives in i18n keys so it follows the language switcher.

const CARDS = [
  { icon: TrendingUp, t: "seo_card1_t", d: "seo_card1_d" },
  { icon: ShieldCheck, t: "seo_card2_t", d: "seo_card2_d" },
  { icon: PackageCheck, t: "seo_card3_t", d: "seo_card3_d" },
  { icon: XOctagon, t: "seo_card4_t", d: "seo_card4_d" },
  { icon: Target, t: "seo_card5_t", d: "seo_card5_d" },
  { icon: Cpu, t: "seo_card6_t", d: "seo_card6_d" },
];

// Render bold markdown (**text**) into <strong> spans inside translation strings.
function renderRich(str) {
  if (!str) return null;
  const parts = str.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? (
      <strong key={i} className="text-white">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

// Strip ** markers without bolding (used inside elements that already have their own emphasis style).
function stripMd(str) {
  return (str || "").replace(/\*\*([^*]+)\*\*/g, "$1");
}

export default function SEOContent() {
  const { t } = useI18n();
  return (
    <section
      aria-label={t("seo_h1_a") + " " + t("seo_h1_b")}
      className="relative py-20 sm:py-24 border-t border-[#23232a]/60"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[color:var(--gold)]/40 to-transparent" />
      <div className="absolute -top-32 right-0 w-[420px] h-[420px] rounded-full bg-[color:var(--gold)] opacity-[0.05] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl">
          <span className="chip">
            <BookOpen className="w-3 h-3 text-[color:var(--gold)]" />
            {t("seo_chip")}
          </span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5">
            {stripMd(t("seo_h1_a"))}{" "}
            <span className="text-[color:var(--gold)] glow-gold">{stripMd(t("seo_h1_b"))}</span>
          </h2>
          <p className="text-[color:var(--ink-dim)] mt-5 leading-relaxed text-[14px] sm:text-base max-w-2xl">
            {renderRich(t("seo_intro"))}
          </p>
        </div>

        {/* Card grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-10 sm:mt-12">
          {CARDS.map(({ icon: Icon, t: titleKey, d: descKey }) => (
            <article
              key={titleKey}
              className="panel p-5 sm:p-6 relative overflow-hidden group transition-colors hover:border-[color:var(--gold)]/40"
            >
              <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[color:var(--gold)] opacity-[0.05] blur-2xl group-hover:opacity-[0.12] transition-opacity" />
              <div className="relative">
                <div
                  className="w-10 h-10 rounded-xl grid place-items-center mb-4 border border-[color:var(--gold)]/30"
                  style={{ background: "linear-gradient(135deg, rgba(245,208,106,.18), rgba(138,106,21,.08))" }}
                >
                  <Icon className="w-5 h-5 text-[color:var(--gold)]" />
                </div>
                <h3 className="font-display font-bold text-[15px] sm:text-base leading-snug">
                  {stripMd(t(titleKey))}
                </h3>
                <p className="text-[13px] sm:text-[13.5px] text-[color:var(--ink-dim)] mt-3 leading-relaxed">
                  {renderRich(t(descKey))}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Pull quote */}
        <div className="mt-10 sm:mt-12 panel p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-[color:var(--gold)] opacity-[0.06] blur-3xl pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-6 items-center">
            <div className="lg:col-span-2 flex lg:justify-center">
              <span
                className="font-display font-black text-[72px] sm:text-[96px] leading-none text-[color:var(--gold)] glow-gold"
                aria-hidden="true"
              >
                &ldquo;
              </span>
            </div>
            <div className="lg:col-span-10">
              <p className="font-display text-lg sm:text-xl lg:text-[22px] leading-snug text-white">
                {stripMd(t("seo_quote"))}
              </p>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mt-4">
                {t("seo_quote_author")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
